import { createContext, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { ArrowLeft, ArrowRight } from 'react-feather';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Col,
  Descriptions,
  Divider,
  Modal,
  notification,
  Row,
  Skeleton,
  Steps,
} from 'antd';

import { queryClient } from '@/Root';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import {
  useDeleteQuotation,
  useQuotation,
} from '@/core/domain/stock/Quotations/Quotations.hooks';
import TabContentWrapper from '@/components/_common/TabContentWrapper';

import { PageProps } from '@/types/PageProps';

import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import UpdateQuotationModal from '@/components/Movements/Quotations/UpdateQuotationModal';
import QuotationReportModal from '@/components/Movements/Quotations/QuotationReportModal';
import {
  Quotation,
  QuotationItem,
  QuotationStatus,
  QuotationStatusColor,
  QuotationStatusLabel,
  QuotationSupplier,
} from '@/core/domain/stock/Quotations/Quotations.types';
import { useContext } from 'react';
import QuotationDetailsItemsPage from './QuotationDetailsItemsPage';
import QuotationDetailsSuppliersPage from './QuotationDetailsSuppliersPage';
import QuotationDetailsOffersConfigurationPage from './QuotationDetailsOffersConfigurationPage';
import QuotationDetailsRequestQuotationPage from './QuotationDetailsRequestQuotationPage';
import QuotationPurchaseRequestPage from './QuotationPurchaseRequestPage';
import QuotationDetailsGeneratePurchasePage from './QuotationDetailsGeneratePurchasePage';
import { useDisclosure, useWindowSize } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import {
  QuotationConfigurationResponse,
  QuotationsConfigurationQuery,
} from '@/core/domain/stock/QuotationsOffers/QuotationsOffers.types';
import StatusTag from '@/components/_common/StatusTag';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { PageResultStock } from '@/core/request/PageResultStock';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import ButtonBack from '@/components/_common/ButtonBack';
import usePageSearchHistory from '@/hooks/pageSearchHistory';

const QuotationContext = createContext(
  {} as (Quotation & { disabled: boolean }) | undefined,
);
export const useQuotationContext = () => useContext(QuotationContext);

const QuotationDetailsPage = ({ route }: PageProps) => {
  const { isMobile } = useWindowSize();
  const intl = useIntl();
  const history = useHistory();
  const { setParamLabel } = useBreadcrumbs();
  const { quotationId } = useRouteMatch<{ quotationId: string }>().params;
  const [step, setStep] = useState<number>(0);

  const { getSearch } = usePageSearchHistory();
  const path = '/app/movements/quotations';
  const pathWithSearch = path + getSearch(path);

  const { data: quotation, isFetching } = useQuotation(+quotationId);

  const deleteMutation = useDeleteQuotation();

  const [reportModalOpen, actionReportModalOpen] = useDisclosure();

  const [updateQuotationModal, updateQuotationModalAction] =
    useDisclosure(false);

  useEffect(() => {
    if (quotation) {
      setParamLabel(
        ':quotationId',
        `${quotation?.result?.company.name}, ${moment(
          quotation?.result?.date,
        ).format('DD/MM/YYYY')}`,
      );
    }
  }, [quotation]);

  const steps = [
    <QuotationDetailsItemsPage key={0} />,

    <QuotationDetailsSuppliersPage key={1} />,

    <QuotationDetailsOffersConfigurationPage key={2} />,

    <QuotationDetailsRequestQuotationPage key={3} />,

    <QuotationPurchaseRequestPage key={4} />,

    <QuotationDetailsGeneratePurchasePage key={5} />,
  ];

  useEffect(() => {
    if (quotation) {
      if (quotation?.result?.status === QuotationStatus.AwaitingOffers) {
        setStep(3);
      }

      if (quotation?.result?.status === QuotationStatus.Answered) {
        setStep(4);
      }

      if (
        quotation?.result?.status === QuotationStatus.WinnersSelected ||
        quotation?.result?.status === QuotationStatus.Done
      ) {
        setStep(5);
      }
    }
  }, [quotation]);

  const handleQuotationItemsNextClick = () => {
    const items = queryClient.getQueryData<PageResultStock<QuotationItem>>([
      'quotations',
      +quotationId,
      'items',
    ]);

    if (items && items?.result?.data.length) {
      setStep((prev) => prev + 1);
    } else {
      notification.error({
        message: intl.formatMessage({
          id: 'quotations.details.items.nextError.message',
        }),
        description: intl.formatMessage({
          id: 'quotations.details.items.nextError.description',
        }),
      });
    }
  };

  const handleSuppliersNextClick = () => {
    const item = queryClient.getQueryData<PageResultStock<QuotationSupplier>>([
      'quotations',
      Number(quotationId),
      'suppliers',
    ]);

    if (item && item?.result?.data.length) {
      setStep((prev) => prev + 1);
    } else {
      notification.error({
        message: intl.formatMessage({
          id: 'quotations.details.suppliers.nextError.message',
        }),
        description: intl.formatMessage({
          id: 'quotations.details.suppliers.nextError.description',
        }),
      });
    }
  };

  const handleOfferConfigurationNextClick = () => {
    const configuration = flatInfiniteResult(
      queryClient.getQueryData([
        'quotations',
        'offers',
        'configuration',
        { quotationId: Number(quotationId) } as QuotationsConfigurationQuery,
      ]),
    ) as QuotationConfigurationResponse[];

    let next = false;

    configuration?.forEach((item) => {
      item.suppliers.forEach((supplier) => {
        if (supplier.configured) next = true;
      });
    });

    if (next) {
      if (
        quotation?.result?.status === QuotationStatus.Answered ||
        quotation?.result?.status === QuotationStatus.WinnersSelected ||
        quotation?.result?.status === QuotationStatus.Done
      ) {
        setStep((prev) => prev + 2);
      } else {
        setStep((prev) => prev + 1);
      }
    } else {
      notification.error({
        message: intl.formatMessage({
          id: 'quotations.details.offersConfiguration.nextError.message',
        }),
        description: intl.formatMessage({
          id: 'quotations.details.offersConfiguration.nextError.description',
        }),
      });
    }
  };

  const handleNextClick = () => {
    switch (step) {
      case 0:
        handleQuotationItemsNextClick();
        break;
      case 1:
        handleSuppliersNextClick();
        break;
      case 2:
        handleOfferConfigurationNextClick();
        break;
      case 4:
        setStep((prev) => prev + 1);
        break;
      default:
        break;
    }
  };

  const handlePurchaseRequestNextClick = () => {
    if (
      quotation?.result?.status === QuotationStatus.Answered ||
      quotation?.result?.status === QuotationStatus.WinnersSelected ||
      quotation?.result?.status === QuotationStatus.Done
    ) {
      setStep((prev) => prev - 2);
    } else {
      setStep((prev) => prev - 1);
    }
  };

  const handleBackClick = () => {
    switch (step) {
      case 0:
        break;
      case 1:
        setStep((prev) => prev - 1);
        break;
      case 2:
        setStep((prev) => prev - 1);
        break;
      case 3:
        setStep((prev) => prev - 1);
        break;
      case 4:
        handlePurchaseRequestNextClick();
        break;
      case 5:
        setStep((prev) => prev - 1);
        break;
      default:
        break;
    }
  };

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'quotations.remove' }),
      content: `${intl.formatMessage({
        id: 'quotations.remove.alert',
      })} ${quotation?.result?.company.name}, ${moment(
        quotation?.result?.date,
      ).format('DD/MM/YYYY')}`,
      onOk: async () => {
        // Fetch Service to remove area item.
        await deleteMutation.mutateAsync(+quotationId);

        history.push('/app/movements/quotations');
      },
      okType: 'danger',
    });
  };

  return (
    <QuotationContext.Provider
      value={
        quotation?.result
          ? {
              ...quotation?.result,
              disabled:
                quotation?.result?.status !== QuotationStatus.Configuring,
            }
          : undefined
      }
    >
      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <SpaceForButton>
            <Button block onClick={actionReportModalOpen.open}>
              <FormattedMessage id="commons.entity.report" />
            </Button>

            <Button
              block
              danger
              onClick={handleRemoveClick}
              disabled={
                !hasPermissions([Authorities.MOVIMENTATION_QUOTATION_DELETE])
              }
            >
              <FormattedMessage id="commons.entity.remove" />
            </Button>

            <Button
              block
              onClick={updateQuotationModalAction.open}
              disabled={
                quotation?.result?.status !== QuotationStatus.Configuring ||
                !hasPermissions([Authorities.MOVIMENTATION_QUOTATION_EDIT])
              }
            >
              <FormattedMessage id="commons.entity.update" />
            </Button>
          </SpaceForButton>
        }
      >
        <div>
          <Skeleton active loading={isFetching}>
            <section>
              <Descriptions column={isMobile ? 1 : 2}>
                <Descriptions.Item
                  label={intl.formatMessage({ id: 'quotations.form.status' })}
                >
                  {typeof quotation?.result?.status == 'number' ? (
                    <StatusTag
                      color={QuotationStatusColor[quotation?.result?.status]}
                      label={intl.formatMessage({
                        id: `quotations.${
                          QuotationStatusLabel[quotation?.result?.status]
                        }`,
                      })}
                    />
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
                <Descriptions.Item
                  label={intl.formatMessage({ id: 'quotations.form.date' })}
                >
                  {moment(quotation?.result?.date).format('DD/MM/YYYY')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={intl.formatMessage({ id: 'quotations.form.company' })}
                >
                  {quotation?.result?.company.name}
                </Descriptions.Item>
                <Descriptions.Item
                  label={intl.formatMessage({ id: 'quotations.form.employee' })}
                >
                  {quotation?.result?.employee.name}
                </Descriptions.Item>
              </Descriptions>
            </section>

            <Divider style={{ margin: '8px 0 16px 0' }} />

            <Row>
              <Col span={24} lg={5}>
                <Steps
                  type="default"
                  current={step}
                  size="small"
                  direction="vertical"
                >
                  <Steps.Step
                    title={intl.formatMessage({
                      id: 'quotations.details.items',
                    })}
                  />

                  <Steps.Step
                    title={intl.formatMessage({
                      id: 'quotations.details.suppliers',
                    })}
                  />

                  <Steps.Step
                    title={intl.formatMessage({
                      id: 'quotations.details.offersConfiguration',
                    })}
                  />

                  <Steps.Step
                    title={intl.formatMessage({
                      id: 'quotations.details.sendQuotationRequest',
                    })}
                  />

                  <Steps.Step title="Solicitação de Compra" />

                  <Steps.Step
                    title={intl.formatMessage({
                      id: 'quotations.details.generatePurchase',
                    })}
                  />
                </Steps>
              </Col>

              <Col span={24} lg={19}>
                {steps[step]}
              </Col>
            </Row>

            <Divider />

            <SpaceForButton justifyContent="space-between">
              {step !== 0 ? (
                <Button
                  block
                  onClick={handleBackClick}
                  disabled={
                    quotation?.result?.status ===
                      QuotationStatus.AwaitingOffers ||
                    quotation?.result?.status === QuotationStatus.Answered
                  }
                >
                  <AntdFeatherIcon icon={<ArrowLeft />} size={12} />
                  <FormattedMessage id="commons.modal.button.back" />
                </Button>
              ) : (
                <ButtonBack path={pathWithSearch} />
              )}

              {step !== 5 ? (
                <Button
                  block
                  onClick={handleNextClick}
                  disabled={
                    quotation?.result?.status ===
                      QuotationStatus.AwaitingOffers ||
                    quotation?.result?.status === QuotationStatus.Answered ||
                    step === 3
                  }
                >
                  <AntdFeatherIcon icon={<ArrowRight />} size={12} />
                  <FormattedMessage id="commons.modal.button.next" />
                </Button>
              ) : (
                <div />
              )}
            </SpaceForButton>
          </Skeleton>
        </div>

        <UpdateQuotationModal
          open={updateQuotationModal}
          onCancel={updateQuotationModalAction.close}
          quotation={quotation?.result}
        />

        <QuotationReportModal
          visible={reportModalOpen}
          handleVisible={actionReportModalOpen.toggle}
        />
      </TabContentWrapper>
    </QuotationContext.Provider>
  );
};

export default QuotationDetailsPage;
